import { Injectable } from '@angular/core';
import { defer, identity, interval, Observable } from 'rxjs';
import { take, takeWhile } from 'rxjs/operators';
import { EnvironmentService } from 'src/app/shared/services/environment.service';
import { PlatformMethod, platformMethodImplementations } from 'src/app/shared/services/offline/platform-methods';
import { PlatformService, PlatformType } from 'src/app/shared/services/offline/platform.service';
import { IDotNet, IDotNetObject } from '../models/dotnet';

declare let DotNet: IDotNet;
@Injectable({
  providedIn: 'root'
})
export class MauiPlatformService extends PlatformService {

  DotNetObject: IDotNetObject | undefined;
  JSObject : any;

  constructor(private environmentService: EnvironmentService) {
    super();
    this._platform = PlatformType.MAUI;
    this.JSObject = DotNet.createJSObjectReference(this);
    // poll every 500ms 10 times, then every 30s afterwards, until dot net object retrieved  
    this.pollDotNetObject(500, 10);
    this.pollDotNetObject(30000);
  }

  private async pollDotNetObject(period: number, count?: number) {
    // poll until retrieve dot net object or exceed count
    interval(period).pipe(
      takeWhile(() => !this.isDotNetObjectAvailable()),
      (count ? take(count) : identity))
      .subscribe(async () => {
        this.retrieveDotNetObject();
      });
  }

  private async retrieveDotNetObject() {
    if (DotNet) {
      // invoke static .net method to retrieve object reference
      console.log('attempt to retrieve maui dot net instance');
      this.DotNetObject = await DotNet.invokeMethodAsync<IDotNetObject>('TechPubs.MAUI', 'OnUIReady', this.JSObject);

      // dot net object retrieved
      if (this.isDotNetObjectAvailable()) {
        console.log('retrieved maui dot net instance', this.DotNetObject);
        this._platformInitialized.next(true);
      }
    }
  }

  private isDotNetObjectAvailable(): boolean {
    return this.DotNetObject ? true : false;
  }

  private invokeMethodAsync<T>(methodIdentifier: string, ...args: any[]): Promise<T> {
    // use dot net object to invoke instance methods
    if (this.isDotNetObjectAvailable()) {
      return this.DotNetObject.invokeMethodAsync(methodIdentifier, ...args);
    }
    throw new Error('Dot Net Instance Not Available');
  }

  invokePlatformMethod<T>(method: PlatformMethod, ...args: any): Observable<T> {
    let implementation = platformMethodImplementations[method].maui;
    console.log(`invoke platform method: ${ method }; implementation: ${ implementation }`);

    // create lazy observable - defer invoking .net method until .subscribe() is called
    return defer(() => this.invokeMethodAsync<T>(implementation, ...args));
  }

  /**
   *
   * @param filePath The relative filepath to the file to write. Forward slashes are recommended.
   * @param fileBuffer A file buffer to write to the specified path.
   */
  writeFile(filePath: string, fileBuffer: Uint8Array): Observable<boolean> {
    const base64String = btoa(String.fromCharCode(...fileBuffer));
    return defer(() => this.invokeMethodAsync<boolean>('WriteFile', filePath, base64String));
  }

  /**
   *
   * @param filePath The relative filepath to the file to read. Forward slashes are recommended.
   */
  readFile(filePath: string): Observable<Uint8Array> {
    return defer(() => this.invokeMethodAsync<Uint8Array>('ReadFile', filePath));
  }

  /**
   *
   * @param filePath The relative filepath to the file/folder to verify. Forward slashes are recommended.
   */
  checkExists(filePath: string): Observable<boolean> {
    return defer(() => this.invokeMethodAsync<boolean>('CheckFileExists', filePath));
  }

  /**
   *
   * @param filePath The relative filepath to the file to remove. Forward slashes are recommended.
   */
  removeFile(filePath: string): Observable<boolean> {
    return defer(() => this.invokeMethodAsync<boolean>('RemoveFile', filePath));
  }

  /**
   *
   * @param folderPath The relative path to the folder to create. Forward slashes are recommended.
   */
  createDirectory(folderPath: string): Observable<boolean> {
    return defer(() => this.invokeMethodAsync<boolean>('CreateDirectory', folderPath));
  }

  /**
   *
   * @param folderPath The relative path to the folder to recursively delete. Forward slashes are recommended.
   */
  removeDirectory(folderPath: string): Observable<boolean> {
    return defer(() => this.invokeMethodAsync<boolean>('RemoveDirectory', folderPath));
  }

  /**
   * @param started The status of the background session.
   */
  receiveBackgroundSessionStarted(started: boolean): void {
    this._backgroundSessionStarted.next(started);
  }

  /**
   * @param content The content of the initial index.
   */
  receiveInitialIndexDownloadStatus(content: Uint8Array): void {
    this._initialIndexDownloadStatusReceived.next(content);
  }

  /**
   * 
   * @param numCalls The number of calls completed.
   */
  receiveInitialZipDownloadStatus(numCalls: number): void {
    this._initialZipDownloadStatusReceived.next(numCalls);
  }

  /**
   * 
   * @param fileName The file name of the download file.
   * @param isComplete Indicates whether the download is complete.
   */
  receiveDownloadFileStatus(status: string, isComplete: boolean): void { 
    this._downloadFileStatusReceived.next({ FileName: status, IsComplete: isComplete }); // not passing statusdto object directly due to serialization issues when calling js from c#
  }
}
